import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  alpha,
  Modal,
  Backdrop,
  Fade,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle,
  Star,
  Business,
  Group,
  TrendingUp,
  WhatsApp,
  Campaign,
  RocketLaunch,
  Phone,
  Email,
} from "@mui/icons-material";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

// Social media logos for floating background
const socialLogos = [
  {
    name: "WhatsApp",
    url: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg",
  },
  {
    name: "Facebook",
    url: "https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg",
  },
  {
    name: "Twitter",
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/X_logo_2023_original.svg/1280px-X_logo_2023_original.svg.png",
  },
  {
    name: "Instagram",
    url: "https://upload.wikimedia.org/wikipedia/commons/e/e7/Instagram_logo_2016.svg",
  },
];

// Maps the billing cycle key used in this component to the number of
// months that should be sent to the wallet-recharge validity flow.
const BILLING_CYCLE_MONTHS = {
  quarter: 3,
  halfYear: 6,
  year: 12,
};

const FloatingIcon = ({ iconUrl, name, top, left, delay }) => {
  return (
    <Box
      component="img"
      src={iconUrl}
      alt={name}
      sx={{
        position: "absolute",
        top: `${top}%`,
        left: `${left}%`,
        width: 40,
        height: 40,
        opacity: 0.15,
        animation: `float 8s ease-in-out ${delay}s infinite`,
        "@keyframes float": {
          "0%": { transform: "translateY(0) rotate(0deg)" },
          "50%": { transform: "translateY(-20px) rotate(10deg)" },
          "100%": { transform: "translateY(0) rotate(0deg)" },
        },
      }}
    />
  );
};

const PricingPlans = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("quarter");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [icons, setIcons] = useState([]);
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    // Generate random positions for floating icons
    const generatedIcons = socialLogos.flatMap((logo, index) => {
      const iconsForLogo = [];
      for (let i = 0; i < 4; i++) {
        iconsForLogo.push({
          ...logo,
          top: Math.random() * 100,
          left: Math.random() * 100,
          delay: Math.random() * 5,
          id: `${logo.name}-${i}`,
        });
      }
      return iconsForLogo;
    });
    setIcons(generatedIcons);

    // Fetch plans from backend
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINT}/root/pricing-plans`, {
        method: "GET",
        ...getHeader(),
      });
      const data = await response.json();

      if (response.ok) {
        // Filter only active plans and sort by order
        const activePlans = (data.plans || [])
          .filter((plan) => plan.active)
          .sort((a, b) => a.order - b.order);
        setPlans(activePlans);
      } else {
        setError(data.message || "Failed to fetch plans");
      }
    } catch (err) {
      setError("Error connecting to server");
      // Fallback to local data
      setPlans(getLocalPlans());
    } finally {
      setLoading(false);
    }
  };

  // Fallback local plans data in case API fails
  const getLocalPlans = () => {
    return [
      {
        _id: "growth",
        name: "Growth",
        description: "Ideal for growing businesses",
        features: [
          "Everything in Starter",
          "Priority support",
          "Advanced analytics",
          "Custom branding",
          "AI campaign optimization",
        ],
        popular: false,
        icon: "TrendingUp",
        color: "#128C7E",
        billingCycles: {
          quarter: { price: 4500, contactLimit: 5000 },
          halfYear: { price: 9000, contactLimit: 12000 },
          year: { price: 16000, contactLimit: 30000 },
        },
        messageRates: {
          marketing: 89,
          utility: 20,
        },
        active: true,
        order: 1,
      },
      {
        _id: "scale",
        name: "Scale",
        description: "For established businesses scaling up",
        features: [
          "Everything in Growth",
          "Dedicated account manager",
          "API access",
          "Custom integrations",
          "Advanced AI agents",
        ],
        popular: true,
        icon: "Business",
        color: "#075E54",
        billingCycles: {
          quarter: { price: 6500, contactLimit: 15000 },
          halfYear: { price: 11700, contactLimit: 40000 },
          year: { price: 20800, contactLimit: 100000 },
        },
        messageRates: {
          marketing: 79,
          utility: 11.5,
        },
        active: true,
        order: 2,
      },
      {
        _id: "enterprise",
        name: "Enterprise",
        description: "For large enterprises with unlimited needs",
        features: [
          "Everything in Scale",
          "24/7 premium support",
          "SLA guarantee",
          "Onboarding assistance",
          "Custom AI model training",
        ],
        popular: false,
        icon: "RocketLaunch",
        color: "#34B7F1",
        billingCycles: {
          quarter: { price: 9400, contactLimit: 50000 },
          halfYear: { price: 16920, contactLimit: 200000 },
          year: { price: 30080, contactLimit: 500000 },
        },
        messageRates: {
          marketing: 79,
          utility: 11.5,
        },
        active: true,
        order: 3,
      },
    ];
  };

  const handleBillingChange = (event, newCycle) => {
    if (newCycle !== null) {
      setBillingCycle(newCycle);
    }
  };

  const getBillingText = () => {
    return billingCycle === "year"
      ? "1 Year"
      : billingCycle === "halfYear"
      ? "6 Months"
      : "3 Months";
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      TrendingUp: <TrendingUp sx={{ fontSize: 40 }} />,
      Business: <Business sx={{ fontSize: 40 }} />,
      RocketLaunch: <RocketLaunch sx={{ fontSize: 40 }} />,
    };
    return iconMap[iconName] || <TrendingUp sx={{ fontSize: 40 }} />;
  };

  // Redirects to the wallet recharge page pre-filled for a validity
  // payment, using the selected plan's price for the current billing
  // cycle and the corresponding number of months.
  const getStarted = (plan) => {
    const billingData = plan.billingCycles?.[billingCycle] || {};
    const price = billingData.price || 0;
    const period = BILLING_CYCLE_MONTHS[billingCycle];

    navigate(
      `/home/wallet-recharge?amount=${encodeURIComponent(
        price
      )}&period=${encodeURIComponent(period)}&type=validity`
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: "100vh",
          py: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && plans.length === 0) {
    return (
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: "100vh",
          py: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: "100vh",
        py: 0,
      }}
    >
      {/* Floating background icons */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        {icons.map((icon) => (
          <FloatingIcon
            key={icon.id}
            iconUrl={icon.url}
            name={icon.name}
            top={icon.top}
            left={icon.left}
            delay={icon.delay}
          />
        ))}
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, pb: 4 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error} (Using fallback data)
          </Alert>
        )}

        <Box
          textAlign="center"
          mb={2}
          mt={1}
          sx={{
            py: 2,
            px: 3,
            borderTopLeftRadius: 100,
            borderTopRightRadius: 100,
            mx: "auto",
            background: `linear-gradient(45deg, ${alpha(
              "#25D366",
              0.1
            )}, ${alpha("#34B7F1", 0.1)})`,
            border: `1px solid ${alpha("#25D366", 0.2)}`,
          }}
        >
          <Chip
            icon={<WhatsApp />}
            label="Smart Growth Solutions"
            sx={{
              mb: 2,
              px: 2,
              py: 3,
              fontSize: 20,
              fontWeight: "bold",
              background: `linear-gradient(45deg, #25D366, #128C7E)`,
              color: "white",
              "& .MuiChip-icon": {
                color: "white",
              },
              borderRadius: 2,
            }}
          />
          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{
              background: "linear-gradient(45deg, #075E54 30%, #25D366 90%)",
              backgroundClip: "text",
              textFillColor: "transparent",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: 0.5,
            }}
          >
            Grow Smartly with our WhatsApp Business Automation Service
          </Typography>
          <Typography
            color="text.secondary"
            maxWidth="md"
            mx="auto"
            sx={{
              color: "#1A3C34",
              lineHeight: 1.6,
            }}
          >
            Zero Confusion! - <b>ONE PLAN FOR EVERYONE</b>. Dont Confuse our
            plan with other company we offer what others offer on their ultimate
            plan all features , When your customer base grows you can upgrade
            plan.
          </Typography>
        </Box>

        {/* Billing Toggle */}
        <Box display="flex" justifyContent="center" mb={4}>
          <ToggleButtonGroup
            value={billingCycle}
            exclusive
            onChange={handleBillingChange}
            aria-label="billing cycle"
            aria-describedby="billing-cycle-description"
            sx={{
              bgcolor: "grey.50",
              borderRadius: 3,
              display: "flex",
              gap: 0.5,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <ToggleButton
              value="quarter"
              aria-label="3 months"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                transition: "all 0.2s ease-in-out",
                bgcolor: billingCycle === "quarter" ? "primary.main" : "white",
                color: billingCycle === "quarter" ? "white" : "text.primary",
                borderColor:
                  billingCycle === "quarter" ? "primary.main" : "grey.300",
                "&:hover": {
                  bgcolor:
                    billingCycle === "quarter" ? "primary.dark" : "grey.100",
                  transform: "scale(1.02)",
                },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                3 Months
              </Typography>
            </ToggleButton>
            <ToggleButton
              value="halfYear"
              aria-label="6 months"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                transition: "all 0.2s ease-in-out",
                bgcolor: billingCycle === "halfYear" ? "primary.main" : "white",
                color: billingCycle === "halfYear" ? "white" : "text.primary",
                borderColor:
                  billingCycle === "halfYear" ? "primary.main" : "grey.300",
                "&:hover": {
                  bgcolor:
                    billingCycle === "halfYear" ? "primary.dark" : "grey.100",
                  transform: "scale(1.02)",
                },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                6 Months
              </Typography>
            </ToggleButton>
            <ToggleButton
              value="year"
              aria-label="1 year"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                transition: "all 0.2s ease-in-out",
                bgcolor: billingCycle === "year" ? "primary.main" : "white",
                color: billingCycle === "year" ? "white" : "text.primary",
                borderColor:
                  billingCycle === "year" ? "primary.main" : "grey.300",
                "&:hover": {
                  bgcolor:
                    billingCycle === "year" ? "primary.dark" : "grey.100",
                  transform: "scale(1.02)",
                },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                1 Year
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Pricing Cards - Only show if we have active plans */}
        {plans.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              No active pricing plans available at the moment.
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Please check back later or contact support.
            </Typography>
          </Box>
        ) : (
          <Grid
            container
            spacing={3}
            alignItems="stretch"
            justifyContent="center"
          >
            {plans.map((plan) => {
              const billingData = plan.billingCycles?.[billingCycle] || {};
              const price = billingData.price || 0;
              const contactLimit = billingData.contactLimit || 0;

              return (
                <Grid item xs={12} md={6} lg={3} key={plan._id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      position: "relative",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      border: plan.popular
                        ? `2px solid ${plan.color}`
                        : "2px solid transparent",
                      boxShadow: plan.popular
                        ? `0 8px 16px ${alpha(plan.color, 0.3)}`
                        : theme.shadows[2],
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `0 16px 24px ${alpha(plan.color, 0.4)}`,
                      },
                    }}
                  >
                    {plan.popular && (
                      <Chip
                        icon={<Star color="#d9b748" />}
                        label="MOST POPULAR"
                        sx={{
                          position: "absolute",
                          right: 1,
                          top: 1,
                          fontWeight: "bold",
                          background: `linear-gradient(45deg, ${
                            plan.color
                          }, ${alpha(plan.color, 0.7)})`,
                          color: "white",
                        }}
                      />
                    )}

                    <CardContent
                      sx={{
                        p: 2,
                        flexGrow: 1,
                        background: alpha(plan.color, 0.03),
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={1.5}>
                        <Box sx={{ color: plan.color }}>
                          {getIconComponent(plan.icon)}
                        </Box>
                        <Typography
                          variant="h4"
                          component="h2"
                          fontWeight="bold"
                          ml={1}
                          color={plan.color}
                        >
                          {plan.name}
                        </Typography>
                      </Box>

                      <Box mb={1.5}>
                        <Typography
                          variant="subtitle1"
                          component="h3"
                          fontWeight="bold"
                          color={plan.color}
                        >
                          Marketing Messages
                        </Typography>
                        <Typography variant="body1" color="text.primary">
                          {plan.messageRates?.marketing || 0} Paisa / msg + GST
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          component="h3"
                          fontWeight="bold"
                          color={plan.color}
                          mt={1}
                        >
                          Utility Messages
                        </Typography>
                        <Typography variant="body1" color="text.primary">
                          {plan.messageRates?.utility || 0} Paisa / msg + GST
                        </Typography>
                      </Box>

                      <Chip
                        icon={<Group />}
                        label={`${contactLimit.toLocaleString()} Unique Contacts`}
                        variant="outlined"
                        sx={{
                          mb: 1.5,
                          fontWeight: "medium",
                          borderColor: plan.color,
                          color: plan.color,
                        }}
                      />

                      <Box mb={2} textAlign="center">
                        <Typography
                          variant="h3"
                          component="h3"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          ₹{price.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          for {getBillingText()}
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {plan.description}
                      </Typography>

                      <Box component="ul" sx={{ pl: 0, mb: 2 }}>
                        {plan.features?.map((feature, index) => (
                          <Box
                            component="li"
                            key={index}
                            display="flex"
                            alignItems="center"
                            mb={0.5}
                            sx={{ listStyle: "none" }}
                          >
                            <CheckCircle
                              sx={{ color: plan.color, fontSize: 18, mr: 1 }}
                            />
                            <Typography variant="body2">{feature}</Typography>
                          </Box>
                        ))}
                      </Box>

                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => getStarted(plan)}
                        size="large"
                        sx={{
                          mt: "auto",
                          fontWeight: "bold",
                          py: 1.5,
                          background: `linear-gradient(45deg, ${
                            plan.color
                          }, ${alpha(plan.color, 0.7)})`,
                          "&:hover": {
                            background: `linear-gradient(45deg, ${alpha(
                              plan.color,
                              0.9
                            )}, ${plan.color})`,
                            boxShadow: `0 4px 8px ${alpha(plan.color, 0.4)}`,
                          },
                        }}
                      >
                        Get Started
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Additional Info */}
        <Box
          textAlign="center"
          mt={4}
          p={4}
          sx={{
            borderRadius: 2,
            background: `linear-gradient(45deg, ${alpha(
              "#25D366",
              0.1
            )}, ${alpha("#34B7F1", 0.1)})`,
            border: `1px solid ${alpha("#25D366", 0.2)}`,
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="bold"
            color="#075E54"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Campaign sx={{ mr: 1, fontSize: 28 }} />
            All Plans Include
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.6, maxWidth: 600, mx: "auto" }}
          >
            Immediate activation with no verification required • AI-powered
            campaign management • WhatsApp Business API access • Real-time
            analytics • 99.9% uptime guarantee
          </Typography>
          <Button
            color="primary"
            variant="outlined"
            sx={{ mt: 3, fontSize: "1rem", px: 3 }}
            onClick={handleOpen}
          >
            Need a custom plan? Contact our team
          </Button>
        </Box>

        <Modal
          open={open}
          onClose={handleClose}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 500,
            },
          }}
        >
          <Fade in={open}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "90%", sm: 400 },
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                fontWeight="bold"
                color="#075E54"
                sx={{ mb: 3 }}
              >
                Contacts
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  mb: 2,
                  px: 2,
                }}
              >
                <Business sx={{ mr: 1.5, color: "#25D366", fontSize: 24 }} />
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  Company: Smart Growth Solutions
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  mb: 2,
                  px: 2,
                }}
              >
                <Phone sx={{ mr: 1.5, color: "#25D366", fontSize: 24 }} />
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  Phone: +91 97511 02074
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  mb: 2,
                  px: 2,
                }}
              >
                <Email sx={{ mr: 1.5, color: "#25D366", fontSize: 24 }} />
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  Email: support@smartgrowthai.com
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2, fontSize: "1rem", px: 3 }}
                onClick={handleClose}
              >
                Close
              </Button>
            </Box>
          </Fade>
        </Modal>
      </Container>
    </Box>
  );
};

export default PricingPlans;
